import { useCallback, useEffect, useRef } from 'react'
import { isEmpty } from 'lodash'
import { differenceInSeconds } from 'date-fns'
import { createInitFilter } from '@utils/filters'
import { Option, SelectProps } from '@organisms/Select'
import { FilterState } from '@hooks/useItemsFilters'
import useSearchParam from '@hooks/useSearchParam'
import { FilterComponentProps } from './index'

const hasPassedMoreTime = (startTime: Date) => {
  const currentTime = new Date()
  const secondsPassed = differenceInSeconds(currentTime, startTime)
  return secondsPassed > 4
}

const useSelectFilterMethods = ({
  filter,
  field,
  onFilter,
  initialFiltersState,
}: FilterComponentProps<SelectProps>) => {
  const { fieldName, fieldNameForSubmit, options, transformValue } = filter
  const param = useSearchParam(fieldName)
  const previousSettedFromParamsTime = useRef<Date>(new Date())

  const resultFieldName = fieldNameForSubmit || fieldName

  const handleFilter = useCallback(
    (newFilter: FilterState, values: Option[] | Option) => {
      return transformValue
        ? onFilter(transformValue(newFilter, values))
        : onFilter(newFilter)
    },
    [onFilter, transformValue]
  )

  const handleSelect = (values: Option[] | Option) => {
    field.onChange?.(values)

    if (!values) {
      const initialStateForFilter = initialFiltersState[resultFieldName]
      handleFilter(
        createInitFilter(resultFieldName, initialStateForFilter || []),
        values
      )
      return
    }

    const resultValues = Array.isArray(values) ? values : [values]
    handleFilter(
      createInitFilter(
        resultFieldName,
        resultValues.map((value) => value?.value)
      ),
      values
    )
  }

  useEffect(() => {
    if (
      param &&
      !isEmpty(options) &&
      !hasPassedMoreTime(previousSettedFromParamsTime.current)
    ) {
      setTimeout(() => {
        try {
          const paramValue = JSON.parse(param)
          handleSelect(paramValue ? [paramValue] : [])
          previousSettedFromParamsTime.current = new Date()
        } catch {
          console.warn('Invalid URI param for:', fieldName)
        }
      }, 500)
    }
  }, [param, options])

  return {
    handleSelect,
    defaultValue: initialFiltersState?.[resultFieldName],
  }
}

export default useSelectFilterMethods
